/**
 * Per-window store session isolation.
 *
 * Multiple store PWAs share origin localStorage. sessionStorage is NOT shared
 * across windows/apps, so each PWA keeps a private copy of its store identity.
 * If another PWA deletes/overwrites shared localStorage keys, this window can
 * restore its own keys from sessionStorage without a full reload.
 */

const SESSION_PREFIX = 'fl_store_session';

const IDENTITY_KEYS = [
  'store-id',
  'type',
  'country-id',
  'store_network',
  'package_type',
  'has_private_package',
  'features_subscriptions',
  'cart_active',
  'liked',
  'store-whats',
];

function sessionBag(slug) {
  return `${SESSION_PREFIX}:${slug}`;
}

function readBag(slug) {
  if (!slug) return null;
  try {
    const raw = sessionStorage.getItem(sessionBag(slug));
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

function writeBag(slug, bag) {
  if (!slug || !bag) return;
  try {
    sessionStorage.setItem(sessionBag(slug), JSON.stringify(bag));
    sessionStorage.setItem('currentStoreSlug', slug);
  } catch (_) {
    /* ignore quota / private mode */
  }
}

/**
 * Snapshot current localStorage identity for this slug into sessionStorage.
 */
export function persistStoreSession(slug, overrides = {}) {
  if (!slug) return null;

  const bag = { ...(readBag(slug) || {}), slug, updatedAt: Date.now() };

  IDENTITY_KEYS.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(overrides, key)) {
      const value = overrides[key];
      if (value != null && value !== 'undefined') {
        bag[key] = typeof value === 'object' ? JSON.stringify(value) : String(value);
      }
      return;
    }
    try {
      const fromLs = localStorage.getItem(`${key}-${slug}`);
      if (fromLs != null && fromLs !== 'undefined') {
        bag[key] = fromLs;
      }
    } catch (_) {
      /* ignore */
    }
  });

  writeBag(slug, bag);
  return bag;
}

/**
 * Read a store identity value: session (this window) → localStorage (shared).
 */
export function getStoreIdentity(key, slug) {
  if (!slug || !key) return null;

  const bag = readBag(slug);
  if (bag && bag[key] != null && bag[key] !== 'undefined' && bag[key] !== '') {
    return bag[key];
  }

  try {
    const fromLs = localStorage.getItem(`${key}-${slug}`);
    if (fromLs != null && fromLs !== 'undefined') return fromLs;
  } catch (_) {
    /* ignore */
  }

  return null;
}

/**
 * Write identity to both session (private) and localStorage (shared).
 */
export function setStoreIdentity(key, value, slug) {
  if (!slug || !key || value == null || value === 'undefined') return;

  const serialized = typeof value === 'object' ? JSON.stringify(value) : String(value);

  try {
    localStorage.setItem(`${key}-${slug}`, serialized);
  } catch (_) {
    /* ignore */
  }

  const bag = readBag(slug) || { slug };
  bag[key] = serialized;
  bag.updatedAt = Date.now();
  writeBag(slug, bag);
}

/**
 * Restore this window's identity keys into shared localStorage if missing/changed.
 * Returns true if any key was restored.
 */
export function restoreStoreSessionToLocalStorage(slug) {
  if (!slug) return false;

  const bag = readBag(slug);
  if (!bag) {
    persistStoreSession(slug);
    return false;
  }

  let restored = false;
  IDENTITY_KEYS.forEach((key) => {
    if (bag[key] == null || bag[key] === '' || bag[key] === 'undefined') return;
    const lsKey = `${key}-${slug}`;
    try {
      const current = localStorage.getItem(lsKey);
      if (current !== bag[key]) {
        localStorage.setItem(lsKey, bag[key]);
        restored = true;
      }
    } catch (_) {
      /* ignore */
    }
  });

  return restored;
}

/**
 * Listen for other windows mutating/removing our keys and re-assert ours.
 */
export function watchStoreSessionIsolation(slug, onRestored) {
  if (!slug || typeof window === 'undefined') return () => {};

  persistStoreSession(slug);

  const reassert = () => {
    const restored = restoreStoreSessionToLocalStorage(slug);
    if (restored && typeof onRestored === 'function') {
      onRestored();
    }
  };

  const onStorage = (event) => {
    if (!event || event.storageArea !== localStorage) return;

    // localStorage.clear() in another document
    if (event.key == null) {
      reassert();
      return;
    }

    const affectsUs = IDENTITY_KEYS.some((k) => event.key === `${k}-${slug}`);
    if (!affectsUs) return;

    // Another PWA deleted or overwrote our shared key — put ours back
    reassert();
  };

  const onVisible = () => {
    if (document.visibilityState === 'visible') reassert();
  };

  window.addEventListener('storage', onStorage);
  document.addEventListener('visibilitychange', onVisible);

  return () => {
    window.removeEventListener('storage', onStorage);
    document.removeEventListener('visibilitychange', onVisible);
  };
}

export { IDENTITY_KEYS };
